import React, { useEffect, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import type { TimeRangePreset, TimeRangeValue } from '../../services/aiUsageApi';
import DatePicker from '../DatePicker';

interface TimeRangePickerProps {
  value: TimeRangeValue;
  onChange: (v: TimeRangeValue) => void;
  className?: string;
  /** Hide the preset label, render compact. */
  compact?: boolean;
}

const PRESETS: { id: TimeRangePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'all_time', label: 'All time' },
  { id: 'custom', label: 'Custom…' },
];

const isoToDay = (iso?: string): string => (iso ? iso.substring(0, 10) : '');
const dayToFromIso = (day: string): string => new Date(`${day}T00:00:00`).toISOString();
const dayToToIso = (day: string): string => new Date(`${day}T23:59:59.999`).toISOString();
const fmtShort = (iso?: string): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '…';

/**
 * TimeRangePicker — preset selector + custom range using the shared custom
 * DatePicker (portal calendar). Custom range edits are held in local DRAFT
 * state and only emitted via onChange when "Apply" is clicked, so consumers
 * never refetch mid-selection.
 *
 * No native <input type=date>, no window.alert/confirm — fully self-contained.
 */
const TimeRangePicker: React.FC<TimeRangePickerProps> = ({ value, onChange, className, compact }) => {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(value.preset === 'custom');
  const [draftFrom, setDraftFrom] = useState(isoToDay(value.fromIso));
  const [draftTo, setDraftTo] = useState(isoToDay(value.toIso));

  // Re-sync draft + mode each time the popover opens (or the external value changes).
  useEffect(() => {
    if (!open) return;
    setCustomMode(value.preset === 'custom');
    setDraftFrom(isoToDay(value.fromIso));
    setDraftTo(isoToDay(value.toIso));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const current = PRESETS.find((p) => p.id === value.preset);
  const buttonLabel =
    value.preset === 'custom' && value.fromIso
      ? `${fmtShort(value.fromIso)} – ${fmtShort(value.toIso)}`
      : current?.label ?? 'Select range';

  const pickPreset = (p: TimeRangePreset) => {
    setCustomMode(false);
    onChange({ preset: p });
    setOpen(false);
  };

  const applyCustom = () => {
    const f = draftFrom || draftTo;
    const t = draftTo || draftFrom;
    if (!f) return;
    onChange({ preset: 'custom', fromIso: dayToFromIso(f), toIso: dayToToIso(t) });
    setOpen(false);
  };

  return (
    <div className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 rounded-lg bg-white border border-stone-200 hover:border-stone-300 transition-colors text-stone-700 text-sm font-medium ${compact ? 'px-3 py-2 min-h-[40px]' : 'px-3.5 py-2.5 min-h-[44px]'}`}
      >
        <Calendar size={14} className="text-stone-500" />
        <span>{buttonLabel}</span>
        <ChevronDown size={14} className="text-stone-400" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 z-40 bg-white border border-stone-200 rounded-xl shadow-lg w-64 p-2">
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {PRESETS.filter((p) => p.id !== 'custom').map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickPreset(p.id)}
                  className={`text-left text-xs px-2.5 py-2 rounded-lg transition-colors min-h-[36px] ${
                    !customMode && value.preset === p.id
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-stone-50 text-stone-700 border border-transparent hover:bg-stone-100'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCustomMode(true)}
              className={`w-full text-left text-xs px-2.5 py-2 rounded-lg transition-colors min-h-[36px] ${
                customMode
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-stone-50 text-stone-700 border border-transparent hover:bg-stone-100'
              }`}
            >
              Custom date range…
            </button>
            {customMode && (
              <div className="mt-2 space-y-2 border-t border-stone-100 pt-2">
                <div>
                  <span className="block text-[11px] text-stone-500 mb-0.5">From</span>
                  <DatePicker
                    value={draftFrom}
                    onChange={setDraftFrom}
                    max={draftTo || undefined}
                    placeholder="Start date"
                  />
                </div>
                <div>
                  <span className="block text-[11px] text-stone-500 mb-0.5">
                    To <span className="text-stone-300">(blank = single day)</span>
                  </span>
                  <DatePicker
                    value={draftTo}
                    onChange={setDraftTo}
                    min={draftFrom || undefined}
                    placeholder="End date"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyCustom}
                  disabled={!draftFrom && !draftTo}
                  className="w-full text-xs font-semibold bg-emerald-600 text-white rounded-lg py-2 hover:bg-emerald-700 disabled:opacity-40 min-h-[36px]"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TimeRangePicker;
